import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BiBriefcase, BiEnvelope, BiGroup, BiPlus } from "react-icons/bi";
import { useToast } from "../../../components/Toast/ToastContext";
import {
  CommissionMode,
  Employee,
  MyInvitations,
  PayBaseType,
  acceptInvitation,
  apiErrorMessage,
  cancelInvitation,
  declineInvitation,
  getEmployees,
  getMyInvitations,
  inviteEmployee,
  leaveEmployer,
  reactivateEmployee,
  removeEmployee,
  resendInvitation,
  updateEmployee,
} from "../../../services/settingsApi";
import {
  PayTermsDraft,
  describePay,
  draftFromTerms,
  normalizePayTerms,
  termsFromDraft,
} from "../staffPay";
import {
  Card,
  ChoiceChips,
  EmptyNote,
  Field,
  Modal,
  dangerButtonClass,
  inputClass,
  linkButtonClass,
  primaryButtonClass,
  secondaryButtonClass,
} from "../settingsUi";
import { currencySymbol } from "../../../services/currency";

const payText = (record: any) =>
  describePay(normalizePayTerms(record?.pay, record?.commission || 0));

/** "Jane's Candles", or the owner's email when the business has no name. */
const businessLabel = (record: any) =>
  record?.invitedBy?.businessName ||
  record?.invitedBy?.name ||
  record?.invitedBy?.email ||
  "a business";

const BASE_OPTIONS: Array<{ value: PayBaseType; label: string }> = [
  { value: "none", label: "No base pay" },
  { value: "hourly", label: "Hourly" },
  { value: "per_event", label: "Per event" },
];

const MODE_OPTIONS: Array<{ value: CommissionMode; label: string }> = [
  { value: "add", label: "On top of base pay" },
  { value: "instead", label: "Instead of base pay" },
  { value: "higher", label: "Whichever is higher" },
];

const PayTermsFields = ({
  value,
  onChange,
}: {
  value: PayTermsDraft;
  onChange: (next: PayTermsDraft) => void;
}) => {
  const set = (patch: Partial<PayTermsDraft>) => onChange({ ...value, ...patch });
  const parsed = termsFromDraft(value);
  const hasCommission = Number(value.commissionPercent) > 0;
  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium text-slate-700">Base pay</p>
        <div className="mt-1.5">
          <ChoiceChips
            options={BASE_OPTIONS}
            value={value.baseType}
            onChange={(baseType) => set({ baseType })}
          />
        </div>
      </div>
      {value.baseType !== "none" && (
        <Field
          label={
            value.baseType === "hourly"
              ? `Hourly rate (${currencySymbol()})`
              : `Pay per event (${currencySymbol()})`
          }
        >
          <input
            className={inputClass}
            inputMode="decimal"
            value={value.baseAmount}
            onChange={(e) => set({ baseAmount: e.target.value })}
            placeholder="0.00"
          />
        </Field>
      )}
      <Field label="Commission (%)" hint="Share of the sales they make. Leave empty for none.">
        <input
          className={inputClass}
          inputMode="decimal"
          value={value.commissionPercent}
          onChange={(e) => set({ commissionPercent: e.target.value })}
          placeholder="0"
        />
      </Field>
      {hasCommission && (
        <>
          <Field
            label={`Sales minimum (${currencySymbol()})`}
            hint="Commission starts once their sales reach this. Leave empty for none."
          >
            <input
              className={inputClass}
              inputMode="decimal"
              value={value.commissionThreshold}
              onChange={(e) => set({ commissionThreshold: e.target.value })}
              placeholder="0.00"
            />
          </Field>
          {value.baseType !== "none" && (
            <div>
              <p className="text-sm font-medium text-slate-700">Commission is paid</p>
              <div className="mt-1.5">
                <ChoiceChips
                  options={MODE_OPTIONS}
                  value={value.commissionMode}
                  onChange={(commissionMode) => set({ commissionMode })}
                />
              </div>
            </div>
          )}
        </>
      )}
      <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
        {"error" in parsed ? parsed.error : describePay(parsed.terms)}
      </p>
    </div>
  );
};

const EmployeeModal = ({
  open,
  employee,
  onClose,
}: {
  open: boolean;
  /** null: inviting someone new. */
  employee: Employee | null;
  onClose: (changed?: boolean) => void;
}) => {
  const { notify } = useToast();
  const [email, setEmail] = useState("");
  const [pay, setPay] = useState<PayTermsDraft>(() => draftFromTerms(normalizePayTerms()));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmail(employee?.email ?? "");
    setPay(draftFromTerms(normalizePayTerms(employee?.pay, employee?.commission || 0)));
  }, [open, employee]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = termsFromDraft(pay);
    if ("error" in parsed) {
      notify(parsed.error, { type: "error" });
      return;
    }
    setSaving(true);
    try {
      if (employee) {
        await updateEmployee(employee._id, employee.role || "employee", parsed.terms);
        notify("Employee updated.", { type: "success" });
      } else {
        await inviteEmployee(email.trim().toLowerCase(), parsed.terms);
        notify(`Invitation sent to ${email.trim()}.`, { type: "success" });
      }
      onClose(true);
    } catch (error) {
      notify(apiErrorMessage(error, "Couldn't save the employee."), { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!employee || !window.confirm(`Remove ${employee.name || employee.email} from your team?`)) {
      return;
    }
    setSaving(true);
    try {
      await removeEmployee(employee._id);
      notify("Employee removed.", { type: "success" });
      onClose(true);
    } catch (error) {
      notify(apiErrorMessage(error, "Couldn't remove the employee."), { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={employee ? "Edit employee" : "Invite employee"}
      onClose={() => onClose()}
      footer={
        <>
          {employee && (
            <button
              type="button"
              className={`${dangerButtonClass} mr-auto`}
              onClick={remove}
              disabled={saving}
            >
              Remove
            </button>
          )}
          <button type="button" className={secondaryButtonClass} onClick={() => onClose()}>
            Cancel
          </button>
          <button type="submit" form="employee-form" className={primaryButtonClass} disabled={saving}>
            {saving ? "Saving…" : employee ? "Save" : "Send invitation"}
          </button>
        </>
      }
    >
      <form id="employee-form" onSubmit={save} className="space-y-5">
        {employee ? (
          <div>
            <p className="font-semibold text-slate-900">{employee.name || employee.email}</p>
            <p className="text-sm text-slate-500">{employee.email}</p>
          </div>
        ) : (
          <Field label="Email" hint="We'll email them an invitation to join your business.">
            <input
              className={inputClass}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              required
            />
          </Field>
        )}
        <PayTermsFields value={pay} onChange={setPay} />
      </form>
    </Modal>
  );
};

type Props = {
  userId: string;
  canUseEmployees: boolean;
  /** False while working in an employer's business. */
  showOwnerTools: boolean;
  onTeamChanged: () => void;
};

const TeamSection = ({ userId, canUseEmployees, showOwnerTools, onTeamChanged }: Props) => {
  const { notify } = useToast();
  const [mine, setMine] = useState<MyInvitations>({ invitations: [], employers: [] });
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Employee | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [invites, team] = await Promise.allSettled([
      getMyInvitations(),
      canUseEmployees ? getEmployees() : Promise.resolve([]),
    ]);
    if (invites.status === "fulfilled") setMine(invites.value);
    if (team.status === "fulfilled") setEmployees(team.value);
    setLoading(false);
  }, [canUseEmployees]);

  useEffect(() => {
    load();
  }, [load]);

  /** Runs one row's action, then reloads; `changesBusinesses` refreshes the switcher. */
  const run = async (
    id: string,
    action: () => Promise<unknown>,
    success: string,
    changesBusinesses = false
  ) => {
    setBusyId(id);
    try {
      await action();
      notify(success, { type: "success" });
      await load();
      if (changesBusinesses) onTeamChanged();
    } catch (error) {
      notify(apiErrorMessage(error, "Something went wrong. Please try again."), { type: "error" });
    } finally {
      setBusyId(null);
    }
  };

  const { invitations, employers } = mine;
  const showEmployees =
    (employers.length === 0 || showOwnerTools) && (canUseEmployees || invitations.length === 0);

  if (loading) {
    return <div className="h-40 animate-pulse rounded-xl border border-slate-200 bg-white" />;
  }

  return (
    <div className="space-y-5">
      {invitations.map((invitation) => (
        <Card
          key={invitation._id}
          icon={BiEnvelope}
          title={`You're invited to join ${businessLabel(invitation)}`}
          description={
            <>
              {invitation?.invitedBy?.email && <>Invited by {invitation.invitedBy.email}. </>}
              {employers.length > 0 &&
                "You'll keep your other jobs and can switch between businesses from the menu."}
            </>
          }
        >
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={primaryButtonClass}
              disabled={busyId === invitation._id || !invitation.invitationToken}
              onClick={() =>
                run(
                  invitation._id,
                  () => acceptInvitation(invitation.invitationToken, userId),
                  `You've joined ${businessLabel(invitation)}.`,
                  true
                )
              }
            >
              Accept
            </button>
            <button
              type="button"
              className={secondaryButtonClass}
              disabled={busyId === invitation._id}
              onClick={() =>
                run(invitation._id, () => declineInvitation(invitation._id), "Invitation declined.")
              }
            >
              Decline
            </button>
          </div>
        </Card>
      ))}

      {employers.length > 0 && (
        <Card icon={BiBriefcase} title="Works for">
          <ul className="divide-y divide-slate-100">
            {employers.map((job) => {
              const ownerId = job?.invitedBy?._id ?? job?.invitedBy;
              return (
                <li key={job._id} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{businessLabel(job)}</p>
                      <p className="text-sm text-slate-500">
                        Owner: {job?.invitedBy?.email || "Unknown"} · Role: {job.role}
                      </p>
                      <p className="mt-2 text-sm text-slate-700">Your pay: {payText(job)}</p>
                      <p className="text-xs text-slate-500">
                        Set by your employer. Ask them if it needs to change.
                      </p>
                    </div>
                    <button
                      type="button"
                      className={dangerButtonClass}
                      disabled={busyId === job._id}
                      onClick={() => {
                        if (!window.confirm(`Stop working for ${businessLabel(job)}?`)) return;
                        run(
                          job._id,
                          () => leaveEmployer(typeof ownerId === "string" ? ownerId : undefined),
                          `You've left ${businessLabel(job)}.`,
                          true
                        );
                      }}
                    >
                      Leave
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {showEmployees &&
        (canUseEmployees ? (
          <Card
            icon={BiGroup}
            title="Employees"
            description="Invite employees, set their pay, and assign them to your events."
            action={
              <button
                type="button"
                className={primaryButtonClass}
                onClick={() => {
                  setEditing(null);
                  setModalOpen(true);
                }}
              >
                <BiPlus size={18} /> Invite
              </button>
            }
          >
            {employees.length === 0 ? (
              <EmptyNote>No employees yet. Invite someone to help at your events.</EmptyNote>
            ) : (
              <ul className="divide-y divide-slate-100">
                {employees.map((emp) => {
                  const pending = emp.invitationStatus === "pending";
                  const inactive = emp.status === "inactive";
                  return (
                    <li
                      key={emp._id}
                      className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium text-slate-900">
                          {emp.name || emp.email}
                          {pending && (
                            <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
                              Invited
                            </span>
                          )}
                          {inactive && (
                            <span className="ml-2 rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">
                              Inactive
                            </span>
                          )}
                        </p>
                        <p className="truncate text-sm text-slate-500">
                          {emp.name ? `${emp.email} · ` : ""}
                          {payText(emp)}
                        </p>
                      </div>
                      <div className="flex gap-3">
                        {pending ? (
                          <>
                            <button
                              type="button"
                              className={linkButtonClass}
                              disabled={busyId === emp._id}
                              onClick={() =>
                                run(emp._id, () => resendInvitation(emp._id), "Invitation resent.")
                              }
                            >
                              Resend
                            </button>
                            <button
                              type="button"
                              className={`${linkButtonClass} text-red-600 hover:text-red-700`}
                              disabled={busyId === emp._id}
                              onClick={() =>
                                run(emp._id, () => cancelInvitation(emp._id), "Invitation cancelled.")
                              }
                            >
                              Cancel invite
                            </button>
                          </>
                        ) : inactive ? (
                          <button
                            type="button"
                            className={linkButtonClass}
                            disabled={busyId === emp._id}
                            onClick={() =>
                              run(emp._id, () => reactivateEmployee(emp._id), "Employee reactivated.")
                            }
                          >
                            Reactivate
                          </button>
                        ) : (
                          <button
                            type="button"
                            className={linkButtonClass}
                            onClick={() => {
                              setEditing(emp);
                              setModalOpen(true);
                            }}
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        ) : (
          <Card
            icon={BiGroup}
            title="Employees"
            description="Invite employees, set their pay, and assign them to events with Pro."
          >
            <Link to="/pricing" className={primaryButtonClass}>
              See Pro
            </Link>
          </Card>
        ))}

      <EmployeeModal
        open={modalOpen}
        employee={editing}
        onClose={(changed) => {
          setModalOpen(false);
          if (changed) load();
        }}
      />
    </div>
  );
};

export default TeamSection;
